'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { IonContent, IonHeader, IonPage } from '@ionic/react';
import { useMediaQuery } from '@shared/hooks';
import { EmptyState, PlusIcon, SwipePager } from '@shared/ui';
import { cn, haptic, shiftDateKey, todayKey } from '@shared/utils';
import type {
  DietEntry,
  DietEntryDraft,
  DietSettings,
  MealSlot,
} from '@diet/types';
import { useDiet } from '@diet/hooks/use-diet.hook';
import { useDietStore } from '@diet/stores/diet.store';
import {
  groupBySlot,
  selectEntriesByDate,
  selectLoggedDates,
  selectRecentItems,
  summarize,
} from '@diet/utils';
import { DailySummaryCard } from './daily-summary-card';
import { DateNavBar } from './date-nav-bar';
import { AppLoadingScreen } from './app-loading-screen';
import { EntryFormSheet } from './entry-form-sheet';
import { MealGroup } from './meal-group';
import { SettingsSheet } from './settings-sheet';
import { UtensilsIcon } from './icons';

const RECENT_ITEM_LIMIT = 10;

type SheetState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; entry: DietEntry };

/**
 * 應用外殼：組合日期導覽、當日總覽、餐別清單與新增／編輯面板。
 *
 * 桌面與手機是**同一份版面**，只在間距與最大寬度上放寬，讓兩端的操作心智模型一致。
 */
export function DietApp() {
  /*
   * 紀錄與設定來自 useDiet（背後是 TanStack Query 的快取），只有「正在看哪一天」
   * 這個純畫面狀態留在 store 裡。
   */
  const {
    status,
    entries,
    settings,
    error,
    retry,
    dismissError,
    addEntry,
    editEntry,
    removeEntry,
    saveSettings,
    estimateNutrition,
    fetchEstimateQuota,
  } = useDiet();
  const selectedDate = useDietStore((state) => state.selectedDate);
  const setSelectedDate = useDietStore((state) => state.setSelectedDate);

  const [sheet, setSheet] = useState<SheetState>({ mode: 'closed' });
  /*
   * 設定面板與記錄面板各自獨立：兩者不會同時開啟，但合併成一個狀態機會讓
   * 「編輯中的紀錄」與「設定」擠在同一個 union 裡，讀起來反而更繞。
   */
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  /* 只在觸控裝置啟用滑動切換日期：滑鼠拖曳的語意是選取文字，
     在桌面攔截它會讓使用者複製不了品項名稱；桌面改用日期列的左右箭頭換日 */
  const isTouch = useMediaQuery('(pointer: coarse)');

  /*
   * 滑動換頁的手勢範圍：整片捲動區，含內容底下的空白處。
   *
   * 掛在 IonContent 而非 IonPage，頂部日期列（IonHeader，在 IonContent 之外）
   * 就自然不吃手勢——它自己的左右箭頭才是那裡的換日方式。
   */
  const contentRef = useRef<HTMLIonContentElement>(null);

  /*
   * 候選品項由表單來問，而不是在這裡算好一份傳下去：它取決於表單當下的餐別與日期，
   * 那兩個值是表單自己的狀態（餐別還會隨時間自動改），這裡看不到。
   */
  const getRecentItems = useCallback(
    (dateKey: string, slot: MealSlot) =>
      selectRecentItems(entries, slot, dateKey, RECENT_ITEM_LIMIT),
    [entries],
  );

  /* 日期選擇面板要標出哪幾天有紀錄。掃過整份 entries，故跟著 entries 快取——
     它每次渲染都重建的話，月曆的每一格查詢都會拿到新的 Set */
  const loggedDates = useMemo(() => selectLoggedDates(entries), [entries]);

  if (status === 'error') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-display text-sm text-foreground">載入紀錄失敗</p>
        <p className="max-w-xs text-xs text-muted-foreground">{error}</p>
        <button
          type="button"
          onClick={retry}
          className={cn(
            'rounded-full bg-accent px-5 py-2',
            'font-display text-sm text-strong text-accent-foreground',
            'transition-all duration-200 ease-out hover:brightness-110 active:scale-[0.98]',
          )}
        >
          重試
        </button>
      </main>
    );
  }

  /* 與 AuthGate 的 fallback 是同一個元件，見 app-loading-screen.tsx */
  if (status !== 'ready') {
    return <AppLoadingScreen />;
  }

  const closeSheet = () => setSheet({ mode: 'closed' });

  /** 切換日期的唯一入口：滑動、日期列、月曆與送出後跳轉都經由此處。 */
  const goToDate = (nextDate: string) => {
    if (nextDate === selectedDate) return;

    setSelectedDate(nextDate);
  };

  /* 一律等後端回覆才關閉面板：失敗時保留使用者填的內容，讓他可以直接再送一次，
     而不是把表單收掉、只留一句錯誤訊息 */
  const handleSubmit = async (draft: DietEntryDraft) => {
    const saved =
      sheet.mode === 'edit'
        ? await editEntry({ ...draft, id: sheet.entry.id })
        : await addEntry(draft);

    if (!saved) {
      haptic('error');
      return;
    }

    /* 送出後跳到該筆所屬日期，使用者才看得到剛記下的紀錄 */
    goToDate(draft.date);
    haptic('success');
    closeSheet();
  };

  /* 同樣等後端回覆才關閉：失敗時把使用者填的目標留在畫面上，讓他可以直接再送一次 */
  const handleSaveSettings = async (next: DietSettings) => {
    const saved = await saveSettings(next);
    haptic(saved ? 'success' : 'error');
    if (saved) setIsSettingsOpen(false);
  };

  const handleDelete = async (id: string) => {
    const removed = await removeEntry(id);
    haptic(removed ? 'success' : 'error');
    if (removed) closeSheet();
  };

  return (
    <IonPage>
      {/*
        IonHeader 在 IonContent 的捲動容器之外，捲動時自然固定。

        **IonHeader 不處理 safe-area**（那是 IonToolbar 的工作）。這裡是自訂
        header，故瀏海讓位由 DateNavBar 自行消費 --ion-safe-area-top。

        `ion-no-border` 關掉 iOS mode 預設的下緣線；DateNavBar 自己有 border-b。
        背景透明化在 @mobile/theme 統一設定（見 ionic-theme.css）。
      */}
      <IonHeader className="ion-no-border">
        <DateNavBar
          dateKey={selectedDate}
          loggedDates={loggedDates}
          onChange={goToDate}
          onOpenSettings={() => setIsSettingsOpen(true)}
        />
      </IonHeader>

      {/*
        IonContent 自帶捲動容器：iOS 的慣性回彈、safe-area 內距、以及後續要接的
        IonRefresher（下拉更新必須掛在 IonContent 底下）都由它提供。
        背景透明化在 @mobile/theme 統一設定（見 ionic-theme.css）。
      */}
      <IonContent ref={contentRef}>
        {/* 資料已載入時的錯誤（例如新增失敗）：既有內容仍有效，故只做提示不換畫面 */}
        {error && (
          <div
            role="alert"
            className={cn(
              'mx-auto mt-3 flex max-w-5xl items-start gap-3 px-4 lg:px-6',
            )}
          >
            <div
              className={cn(
                'flex flex-1 items-start justify-between gap-3 rounded-2xl px-4 py-3',
                'border border-error/30 bg-error-muted',
              )}
            >
              <p className="text-xs leading-relaxed text-foreground">{error}</p>
              <button
                type="button"
                onClick={dismissError}
                className="shrink-0 font-mono text-xs text-muted-foreground hover:text-foreground"
              >
                關閉
              </button>
            </div>
          </div>
        )}

        <main
          className={cn(
            'mx-auto grid max-w-5xl gap-6 pt-5',
            /* 底部留白讓最後一筆不被 FAB 蓋住 */
            'pb-[calc(6rem+var(--ion-safe-area-bottom,0px))]',
            'lg:gap-8 lg:pt-8',
          )}
        >
          {/*
          左右內距移到每一格內部，而非套在軌道上：軌道要能整條橫移，
          外層有內距的話相鄰兩格之間會露出縫隙。
        */}
          <SwipePager
            pageKey={selectedDate}
            getAdjacentKey={shiftDateKey}
            onPage={goToDate}
            enabled={isTouch}
            gestureSurfaceRef={contentRef}
            renderPage={(day) => (
              <DaySection
                dateKey={day}
                entries={entries}
                goal={settings.dailyCalorieGoal}
                onSelectEntry={(entry) => setSheet({ mode: 'edit', entry })}
              />
            )}
          />
        </main>
      </IonContent>

      <button
        type="button"
        onClick={() => {
          haptic('tap');
          setSheet({ mode: 'create' });
        }}
        className={cn(
          'fixed right-5 z-[1200] flex h-13 items-center gap-1.5 rounded-full px-5',
          'bottom-[calc(1.5rem+var(--ion-safe-area-bottom,0px))]',
          'bg-accent font-display text-sm text-strong text-accent-foreground',
          'shadow-glow-md transition-all duration-200 ease-out',
          'hover:brightness-110 active:scale-[0.98]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        )}
      >
        <PlusIcon size={18} />
        記錄
      </button>

      {/*
        IonModal 為常駐元件，以 isOpen 控制開闔。內容於開啟時才掛載，
        表單狀態自然重置，不需要 key。
      */}
      <EntryFormSheet
        isOpen={sheet.mode !== 'closed'}
        entry={sheet.mode === 'edit' ? sheet.entry : null}
        defaultDate={selectedDate}
        getRecentItems={getRecentItems}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onEstimate={estimateNutrition}
        onFetchQuota={fetchEstimateQuota}
        onClose={closeSheet}
      />

      <SettingsSheet
        isOpen={isSettingsOpen}
        settings={settings}
        onSubmit={handleSaveSettings}
        onClose={() => setIsSettingsOpen(false)}
      />
    </IonPage>
  );
}

interface DaySectionProps {
  dateKey: string;
  entries: DietEntry[];
  /** 每日目標熱量，null 為未設定 */
  goal: number | null;
  onSelectEntry: (entry: DietEntry) => void;
}

/**
 * 單一天的內容。
 *
 * 由 `SwipePager` 同時渲染三天（前一天／當天／後一天）以備滑動，故本元件必須能
 * 依傳入的日期自行推導資料，不能仰賴外層針對「選取中那天」算好的結果。
 */
function DaySection({
  dateKey,
  entries,
  goal,
  onSelectEntry,
}: DaySectionProps) {
  const dailyEntries = useMemo(
    () => selectEntriesByDate(entries, dateKey),
    [entries, dateKey],
  );
  const summary = useMemo(() => summarize(dailyEntries), [dailyEntries]);
  const groups = useMemo(() => groupBySlot(dailyEntries), [dailyEntries]);

  const isToday = dateKey === todayKey();

  return (
    <div className="flex flex-col gap-5 px-4 lg:px-6">
      <DailySummaryCard summary={summary} isToday={isToday} goal={goal} />

      {groups.length > 0 ? (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <MealGroup
              key={group.slot}
              group={group}
              onSelectEntry={onSelectEntry}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<UtensilsIcon size={26} />}
          title={isToday ? '今天還沒有紀錄' : '這天沒有紀錄'}
          description="吃了什麼就順手記一筆，一天的加總會出現在上方。"
        />
      )}
    </div>
  );
}
